import { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  Users,
  Target,
  FileText,
  Package,
  BarChart3,
  Settings,
  Shield,
  Star,
  Key,
  GitBranch,
} from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import sidebarLogo from "@/components/utils/Logo transparente Kersales icono.png";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import packageJson from "../../../package.json";

interface BaseRoute {
  name: string;
  href: string;
  icon: any;
  requiredPermission?: string;
  badge?: string | number;
  isPremium?: boolean;
  requiredPlan?: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getUserPlan, can } = useAuth();
  const branding = useSelector((state: RootState) => state.branding);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const plan = getUserPlan();

  const isActiveRoute = useCallback(
    (href: string) => {
      if (href === "/dashboard") {
        return location.pathname === "/dashboard" || location.pathname === "/";
      }
      return (
        location.pathname === href || location.pathname.startsWith(`${href}/`)
      );
    },
    [location.pathname],
  );

  const navigation = useMemo(() => {
    const mainRoutes: BaseRoute[] = [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        requiredPermission: "dashboard.view",
      },
      {
        name: "Clientes",
        href: "/customers",
        icon: Users,
        requiredPermission: "client_companies.read",
      },
      {
        name: "Oportunidades",
        href: "/opportunities",
        icon: Target,
        requiredPermission: "opportunities.read",
      },
      {
        name: "Productos",
        href: "/products",
        icon: Package,
        requiredPermission: "products.read",
      },
      {
        name: "Usuarios",
        href: "/users",
        icon: Users,
        requiredPermission: "users.read",
      },
      {
        name: "Roles",
        href: "/roles",
        icon: Key,
        requiredPermission: "roles.read",
      },
      {
        name: "Pipelines",
        href: "/pipelinesadmin",
        icon: GitBranch,
        requiredPermission: "pipelines.manage",
      },
    ];

    let routes: BaseRoute[] = mainRoutes.filter((route) => {
      return !route.requiredPermission || can(route.requiredPermission);
    });

    return routes.slice(0, 8);
  }, [user?.role?.name, can]);

  const getRoleDisplayName = (role: string | undefined) => {
    if (!role) return "Usuario";

    const roleMap: Record<string, string> = {
      "super admin": "Super Admin",
      admin: "Administrador",
      vendedor: "Vendedor",
      lector: "Lector",
      manager: "Gerente",
    };
    return roleMap[role.toLowerCase()] || role;
  };

  const handleUpgradeClick = useCallback(() => {
    navigate("/planes");
  }, [navigate]);

  const handleUserClick = useCallback(() => {
    navigate("/settings");
  }, [navigate]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-auto">
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl shadow-black/50 px-4 py-3">
          <div className="flex items-center gap-3 min-w-max px-2">
              <div
                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => navigate("/dashboard")}
                title="Ir al Dashboard"
              >
                <img
                  src={sidebarLogo}
                  alt={branding?.name || "Logo"}
                  className="h-12 w-12 object-contain"
                  style={{
                    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
                  }}
                />
                <span className="text-[9px] text-slate-500 font-medium -mt-1">
                  v{packageJson.version}
                </span>
              </div>

            <div className="w-px h-10 bg-slate-700"></div>

            {navigation.map((item, index) => {
              const isActive = isActiveRoute(item.href);
              const isHovered = hoveredIndex === index;
              const Icon = item.icon;

              const scale = isHovered ? 1.3 : 1;
              const translateY = isActive ? -6 : 0;

              return (
                <div
                  key={item.href}
                  className={`relative group transition-all duration-300 ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{
                    transitionDelay: `${index * 30}ms`,
                  }}
                >
                  <Link
                    to={item.href}
                    className="flex flex-col items-center justify-center"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div
                      className={cn(
                        "relative p-3 rounded-xl transition-all duration-300",
                        isActive
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/50"
                          : "bg-slate-800/50 hover:bg-slate-800",
                      )}
                      style={{
                        transform: `scale(${scale}) translateY(${translateY}px)`,
                      }}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 transition-colors",
                          isActive
                            ? "text-white"
                            : "text-slate-400 group-hover:text-white",
                        )}
                      />

                      {item.badge && (
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-slate-900 text-white text-xs border-2 border-slate-900">
                          {typeof item.badge === "number" && item.badge > 9
                            ? "9+"
                            : item.badge}
                        </Badge>
                      )}

                      {isActive && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50"></div>
                      )}
                    </div>
                  </Link>

                  <div
                    className={cn(
                      "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-lg shadow-xl whitespace-nowrap transition-all duration-200 z-[9999] pointer-events-none",
                      isHovered
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2",
                    )}
                  >
                    <p className="text-xs font-medium text-white">
                      {item.name}
                    </p>
                    {item.isDisabled && (
                      <p className="text-xs text-orange-600 font-medium mt-1">
                        Requiere upgrade
                      </p>
                    )}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900/95 border-r border-b border-slate-800 rotate-45"></div>
                  </div>
                </div>
              );
            })}

            <div className="w-px h-10 bg-slate-700"></div>

            <div
              className={`relative group transition-all duration-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: `${navigation.length * 30}ms`,
              }}
            >
              <div
                className="flex items-center gap-2 p-2 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredIndex(navigation.length)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={handleUserClick}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm shadow-lg shadow-slate-900/30 ring-1 ring-white/10">
                  {user?.first_name?.charAt(0) ||
                    user?.email?.charAt(0)?.toUpperCase() ||
                    "U"}
                </div>
              </div>

              <div
                className={cn(
                  "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-lg shadow-xl whitespace-nowrap transition-all duration-200 min-w-[180px]",
                  hoveredIndex === navigation.length
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none",
                )}
              >
                <p className="text-sm font-medium text-white mb-1">
                  {user?.first_name
                    ? `${user.first_name} ${user.last_name || ""}`
                    : user?.name || "Usuario"}
                </p>
                <p className="text-xs text-slate-400 mb-1">{user?.email}</p>
                <p className="text-xs text-blue-400 font-medium mb-2">
                  {getRoleDisplayName(user?.role?.name)}
                </p>

                {plan.type !== "enterprise" && (
                  <Button
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs font-medium py-1 h-7"
                    size="sm"
                    onClick={handleUpgradeClick}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Actualizar Plan
                  </Button>
                )}

                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900/95 border-r border-b border-slate-800 rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
